const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
const FINE_POINTER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';
const PRELOAD_VISIBLE_RATIO = 0.1;
const SCROLL_END_DELAY = 140;
let activeVideo = null;

class CustomProductMediaCollection extends HTMLElement {
  #abortController = null;
  #cards = [];
  #videos = [];
  #track = null;
  #layout = 'grid';
  #visibilityObserver = null;
  #mutationObserver = null;
  #visibilityRatios = new Map();
  #scrollEndTimer = null;
  #isScrolling = false;
  #mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
  #finePointerQuery = window.matchMedia(FINE_POINTER_MEDIA_QUERY);
  #reducedMotionQuery = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY);

  connectedCallback() {
    if (this.#abortController) return;

    this.#layout = this.dataset.mediaLayout === 'carousel' ? 'carousel' : 'grid';
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('pointerover', this.#handlePointerOver, { signal });
    this.addEventListener('pointerout', this.#handlePointerOut, { signal });
    this.addEventListener('pointermove', this.#handleMobilePointerMove, { passive: true, signal });
    this.addEventListener('pointerup', this.#handleMobilePointerEnd, { signal });
    this.addEventListener('pointercancel', this.#handleMobilePointerEnd, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    window.addEventListener('resize', this.#handleResize, { passive: true, signal });
    this.#mobileQuery.addEventListener('change', this.#handlePlaybackModeChange, { signal });
    this.#finePointerQuery.addEventListener('change', this.#handlePlaybackModeChange, { signal });
    this.#reducedMotionQuery.addEventListener('change', this.#handlePlaybackModeChange, { signal });
    document.addEventListener('shopify:section:unload', this.#handleSectionUnload, { signal });

    this.#track = this.querySelector('[data-custom-media-scroll-snap-track]');
    const scrollTarget = this.#layout === 'carousel' && this.#track ? this.#track : window;
    scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true, signal });

    this.#mutationObserver = new MutationObserver(this.#handleMutations);
    this.#mutationObserver.observe(this, { childList: true, subtree: true });
    this.#refreshMedia();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
    this.#clearScrollEndTimer();
    this.#pauseAll();
    this.#visibilityRatios.clear();
    this.#cards = [];
    this.#videos = [];
    this.#track = null;
    this.#isScrolling = false;
  }

  #setupVisibilityObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.#visibilityObserver = new IntersectionObserver(this.#handleVisibilityEntries, {
      threshold: [0, PRELOAD_VISIBLE_RATIO, 0.5, 0.75, 1],
    });

    for (const card of this.#cards) {
      this.#visibilityObserver.observe(card);
    }
  }

  #handleVisibilityEntries = (entries) => {
    for (const entry of entries) {
      this.#visibilityRatios.set(entry.target, entry.intersectionRatio);
      const video = entry.target.querySelector('video.custom-product-media-card__media--video');

      if (entry.intersectionRatio === 0 && video instanceof HTMLVideoElement) {
        this.#pauseVideo(video);
      }

      if (this.#isMobileMode() && entry.intersectionRatio >= PRELOAD_VISIBLE_RATIO) {
        if (video instanceof HTMLVideoElement) this.#prepareVideo(video);
      }
    }

    if (this.#isMobileMode()) {
      if (!this.#isScrolling) this.#scheduleMobileSync(0);
    } else {
      this.#syncDesktopPlayback();
    }
  };

  #handleMutations = () => {
    this.#refreshMedia();
  };

  #handleSectionUnload = (event) => {
    if (!(event.target instanceof Element) || !event.target.contains(this)) return;

    this.disconnectedCallback();
  };

  #handlePointerOver = (event) => {
    if (!this.#isDesktopMode() || !this.#finePointerQuery.matches) return;

    const card = this.#getCard(event.target);
    if (!card || this.#isInternalTransition(card, event.relatedTarget)) return;

    const video = card.querySelector('video.custom-product-media-card__media--video');
    if (video instanceof HTMLVideoElement) this.#playOnly(video);
  };

  #handlePointerOut = (event) => {
    if (!this.#isDesktopMode() || !this.#finePointerQuery.matches) return;

    const card = this.#getCard(event.target);
    if (!card || this.#isInternalTransition(card, event.relatedTarget)) return;
    if (card.contains(document.activeElement)) return;

    const video = card.querySelector('video.custom-product-media-card__media--video');
    if (video instanceof HTMLVideoElement) this.#pauseVideo(video);
  };

  #handleFocusIn = (event) => {
    if (!this.#isDesktopMode()) return;

    const card = this.#getCard(event.target);
    const video = card?.querySelector('video.custom-product-media-card__media--video');
    if (video instanceof HTMLVideoElement) this.#playOnly(video);
  };

  #handleFocusOut = (event) => {
    if (!this.#isDesktopMode()) return;

    const card = this.#getCard(event.target);
    if (!card || this.#isInternalTransition(card, event.relatedTarget)) return;
    if (this.#finePointerQuery.matches && card.matches(':hover')) return;

    const video = card.querySelector('video.custom-product-media-card__media--video');
    if (video instanceof HTMLVideoElement) this.#pauseVideo(video);
  };

  #handleMobilePointerMove = (event) => {
    if (!this.#isMobileMode() || event.pointerType === 'mouse') return;

    this.#isScrolling = true;
    this.#pauseAll();
    this.#clearScrollEndTimer();
  };

  #handleMobilePointerEnd = () => {
    if (!this.#isMobileMode() || !this.#isScrolling) return;

    this.#isScrolling = false;
    this.#scheduleMobileSync(SCROLL_END_DELAY);
  };

  #handleScroll = () => {
    if (!this.#isMobileMode()) return;

    this.#isScrolling = true;
    this.#pauseAll();
    this.#clearScrollEndTimer();
    this.#scrollEndTimer = window.setTimeout(() => {
      this.#scrollEndTimer = null;
      this.#isScrolling = false;
      this.#syncMobilePlayback();
    }, SCROLL_END_DELAY);
  };

  #handleResize = () => {
    this.#pauseAll();
    this.#clearScrollEndTimer();
    this.#scrollEndTimer = window.setTimeout(() => {
      this.#scrollEndTimer = null;
      if (!this.#isScrolling && this.isConnected) this.#syncCurrentMode();
    }, SCROLL_END_DELAY);
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#pauseAll();
      return;
    }

    this.#syncCurrentMode();
  };

  #handlePlaybackModeChange = () => {
    this.#pauseAll();
    this.#clearScrollEndTimer();
    this.#isScrolling = false;
    this.#syncCurrentMode();
  };

  #syncCurrentMode() {
    if (this.#isMobileMode()) {
      this.#scheduleMobileSync(0);
    } else {
      this.#syncDesktopPlayback();
    }
  }

  #syncDesktopPlayback() {
    if (!this.#isDesktopMode() || this.#cannotPlay()) return;

    const focusedCard = this.#getCard(document.activeElement);
    const focusedVideo = focusedCard?.querySelector('video.custom-product-media-card__media--video');
    if (focusedVideo instanceof HTMLVideoElement && this.#isCardVisible(focusedCard)) {
      this.#playOnly(focusedVideo);
      return;
    }

    if (!this.#finePointerQuery.matches) return;

    const hoveredCard = this.#cards.find((card) => card.matches(':hover'));
    const hoveredVideo = hoveredCard?.querySelector('video.custom-product-media-card__media--video');
    if (hoveredVideo instanceof HTMLVideoElement && this.#isCardVisible(hoveredCard)) {
      this.#playOnly(hoveredVideo);
    }
  }

  #scheduleMobileSync(delay) {
    this.#clearScrollEndTimer();
    this.#scrollEndTimer = window.setTimeout(() => {
      this.#scrollEndTimer = null;
      if (!this.#isScrolling) this.#syncMobilePlayback();
    }, delay);
  }

  #syncMobilePlayback() {
    if (!this.#isMobileMode() || this.#cannotPlay() || !this.#visibilityObserver) {
      this.#pauseAll();
      return;
    }

    if (this.#layout === 'carousel') {
      this.#syncCarouselPlayback();
    } else {
      this.#syncGridPlayback();
    }
  }

  #syncCarouselPlayback() {
    if (!this.#track) {
      this.#pauseAll();
      return;
    }

    const visibleCards = this.#cards.filter(
      (card) => (this.#visibilityRatios.get(card) || 0) > 0
    );
    const trackRect = this.#track.getBoundingClientRect();
    const trackCenter = (trackRect.left + trackRect.right) / 2;
    const activeCard = this.#closestCard(visibleCards, (card) => {
      const rect = card.getBoundingClientRect();
      return Math.abs((rect.left + rect.right) / 2 - trackCenter);
    });
    const video = activeCard?.querySelector('video.custom-product-media-card__media--video');

    this.#playOnly(video instanceof HTMLVideoElement ? video : null);
  }

  #syncGridPlayback() {
    const viewportCenterX = window.innerWidth / 2;
    const viewportCenterY = window.innerHeight / 2;
    const visibleVideoCards = this.#cards.filter((card) => {
      if ((this.#visibilityRatios.get(card) || 0) <= 0) return false;
      return card.querySelector('video.custom-product-media-card__media--video') instanceof HTMLVideoElement;
    });
    const activeCard = this.#closestCard(visibleVideoCards, (card) => {
      const rect = card.getBoundingClientRect();
      const deltaX = (rect.left + rect.right) / 2 - viewportCenterX;
      const deltaY = (rect.top + rect.bottom) / 2 - viewportCenterY;
      return Math.hypot(deltaX, deltaY);
    });
    const video = activeCard?.querySelector('video.custom-product-media-card__media--video');

    this.#playOnly(video instanceof HTMLVideoElement ? video : null);
  }

  #closestCard(cards, getDistance) {
    return cards.reduce((closest, card) => {
      if (!closest) return card;
      return getDistance(card) < getDistance(closest) ? card : closest;
    }, null);
  }

  #playOnly(targetVideo) {
    for (const video of this.#videos) {
      if (video !== targetVideo) this.#pauseVideo(video);
    }

    if (!(targetVideo instanceof HTMLVideoElement) || this.#cannotPlay()) return;

    if (activeVideo && activeVideo !== targetVideo) activeVideo.pause();

    this.#prepareVideo(targetVideo);
    activeVideo = targetVideo;
    const playPromise = targetVideo.play();
    if (playPromise) {
      playPromise.catch(() => {
        targetVideo.pause();
        if (activeVideo === targetVideo) activeVideo = null;
      });
    }
  }

  #prepareVideo(video) {
    if (video.dataset.customProductMediaPrepared === 'true') return;

    video.dataset.customProductMediaPrepared = 'true';
    video.preload = 'metadata';
    video.load();
  }

  #pauseAll() {
    for (const video of this.#videos) this.#pauseVideo(video);
  }

  #pauseVideo(video) {
    video.pause();
    if (activeVideo === video) activeVideo = null;
  }

  #refreshMedia() {
    const previousVideos = this.#videos;

    this.#cards = Array.from(this.querySelectorAll('[data-custom-product-media-card]')).filter(
      (card) => card.closest('custom-product-media-collection') === this
    );
    this.#videos = this.#cards
      .map((card) => card.querySelector('video.custom-product-media-card__media--video'))
      .filter((video) => video instanceof HTMLVideoElement);

    for (const video of previousVideos) {
      if (!this.#videos.includes(video)) this.#pauseVideo(video);
    }

    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#visibilityRatios.clear();
    this.#setupVisibilityObserver();
  }

  #getCard(target) {
    if (!(target instanceof Element)) return null;

    const card = target.closest('[data-custom-product-media-card]');
    return card && card.closest('custom-product-media-collection') === this ? card : null;
  }

  #isInternalTransition(card, relatedTarget) {
    return relatedTarget instanceof Node && card.contains(relatedTarget);
  }

  #isMobileMode() {
    return this.#mobileQuery.matches;
  }

  #isDesktopMode() {
    return !this.#mobileQuery.matches;
  }

  #isCardVisible(card) {
    if (!this.#visibilityObserver) return true;

    return (this.#visibilityRatios.get(card) || 0) > 0;
  }

  #cannotPlay() {
    return document.hidden || this.#reducedMotionQuery.matches;
  }

  #clearScrollEndTimer() {
    if (this.#scrollEndTimer === null) return;

    window.clearTimeout(this.#scrollEndTimer);
    this.#scrollEndTimer = null;
  }
}

if (!customElements.get('custom-product-media-collection')) {
  customElements.define('custom-product-media-collection', CustomProductMediaCollection);
}
